import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useSearch } from '@/hooks/useSearch'
import type { SearchCategory, SearchItem } from '@/config/searchData'
import CreateModal from '@/components/modals/CreateModal'
import { productById, describe } from '@/config/products'
import ProductIcon from '@/components/ProductIcon'
import { useVisibleProducts } from '@/hooks/useVisibleProducts'

// Helper function to get language with fallback
function getLanguageKey(lang: string): keyof SearchItem['title'] {
  // Direct match
  if (lang === 'en' || lang === 'zh' || lang === 'zh_CN' || lang === 'jp' || lang === 'ko') {
    return lang as keyof SearchItem['title']
  }
  // Fallback for zh_CN -> zh
  if (lang.startsWith('zh')) {
    return 'zh'
  }
  // Default fallback to en
  return 'en'
}

// Helper function to get text with fallback
function getText<T extends { [key: string]: any }>(
  obj: T,
  lang: string,
  fallbackLang: keyof T
): T[keyof T] | undefined {
  const langKey = getLanguageKey(lang) as keyof T
  return obj[langKey] || obj[fallbackLang]
}

interface HeaderProps {
  sidebarCollapsed: boolean
  onToggleSidebar: () => void
}

export default function Header({ sidebarCollapsed, onToggleSidebar }: HeaderProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showProductsMenu, setShowProductsMenu] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [userMenuPosition, setUserMenuPosition] = useState({ top: 0, right: 0 })
  const [productsMenuPosition, setProductsMenuPosition] = useState({ top: 0, left: 0 })
  const [searchResultsPosition, setSearchResultsPosition] = useState({ top: 0, left: 0, width: 0 })
  const searchRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchResultsRef = useRef<HTMLDivElement>(null)
  const productsMenuRef = useRef<HTMLDivElement>(null)
  const productsButtonRef = useRef<HTMLButtonElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const userMenuContainerRef = useRef<HTMLDivElement>(null)
  const userButtonRef = useRef<HTMLButtonElement>(null)
  const { t, i18n } = useTranslation('header')
  const { t: tSearch } = useTranslation('search')
  const { user, logout } = useAuth()
  const visibleProducts = useVisibleProducts()
  const navigate = useNavigate()
  const searchResults = useSearch(searchQuery, i18n.language)

  // Calculate user menu position when it opens
  useEffect(() => {
    if (showUserMenu && userButtonRef.current) {
      const rect = userButtonRef.current.getBoundingClientRect()
      setUserMenuPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      })
    }
  }, [showUserMenu])

  // Calculate products menu position when it opens
  useEffect(() => {
    if (showProductsMenu && productsButtonRef.current) {
      const rect = productsButtonRef.current.getBoundingClientRect()
      setProductsMenuPosition({
        top: rect.bottom + 8,
        left: rect.left
      })
    }
  }, [showProductsMenu])

  // Calculate search results position when it opens
  useEffect(() => {
    if (isSearchFocused && searchQuery && searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect()
      setSearchResultsPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      })
    }
  }, [isSearchFocused, searchQuery])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside both search input and search results
      const isOutsideSearchInput = searchRef.current && !searchRef.current.contains(event.target as Node)
      const isOutsideSearchResults = searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node)
      if (isOutsideSearchInput && isOutsideSearchResults) {
        setIsSearchFocused(false)
      }

      // Check if click is outside both the button and the menu for products
      const isOutsideProductsButton = productsButtonRef.current && !productsButtonRef.current.contains(event.target as Node)
      const isOutsideProductsMenu = productsMenuRef.current && !productsMenuRef.current.contains(event.target as Node)
      if (isOutsideProductsButton && isOutsideProductsMenu) {
        setShowProductsMenu(false)
      }
      // Check if click is outside both the button and the menu for user
      const isOutsideButton = userButtonRef.current && !userButtonRef.current.contains(event.target as Node)
      const isOutsideMenu = userMenuRef.current && !userMenuRef.current.contains(event.target as Node)
      if (isOutsideButton && isOutsideMenu) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard shortcut for search (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [searchResults])

  // Keyboard navigation
  useEffect(() => {
    if (!isSearchFocused || searchResults.length === 0) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => (prev + 1) % searchResults.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length)
      } else if (e.key === 'Enter' && searchResults.length > 0) {
        e.preventDefault()
        handleSelectResult(searchResults[selectedIndex])
      } else if (e.key === 'Escape') {
        setIsSearchFocused(false)
        searchInputRef.current?.blur()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSearchFocused, searchResults, selectedIndex])

  const handleSelectResult = (item: typeof searchResults[0]) => {
    setSearchQuery('')

    // A product's address is resolved when chosen: the dashboard's depends on the site and the language
    const product = item.category === 'product' ? productById(item.id) : undefined
    const href = product ? product.href(i18n.language) : item.href
    if (product ? product.external : item.external) {
      window.open(href, '_blank', 'noopener,noreferrer')
      // Keep focus for external links since user stays on page
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      navigate(href)
      // Keep focus for internal navigation so user can search again immediately
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }

  const getCategoryIcon = (category: SearchCategory) => {
    switch (category) {
      case 'page':
        return '📄'
      case 'product':
        return '📦'
      case 'data':
        return '💾'
      case 'setting':
        return '⚙️'
      default:
        return '📌'
    }
  }

  const groupedResults = searchResults.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = []
    }
    acc[item.category].push(item)
    return acc
  }, {} as Record<SearchCategory, typeof searchResults>)

  return (
    <header className="z-10 bg-white/95 backdrop-blur-xl shadow-sm border-b border-gray-200/50 px-4 py-1.5 sticky top-0 w-full">
      <div className="flex items-center justify-between">
        {/* Sidebar Toggle + Products Menu + Logo and Title */}
        <div className="flex items-center space-x-2">
          {/* Sidebar Toggle Button */}
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100/50 transition-all duration-200 group"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg className="w-4 h-4 text-gray-600 group-hover:text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {sidebarCollapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
          </button>

          {/* Products Menu */}
          <div className="relative">
            <button
              ref={productsButtonRef}
              onClick={() => setShowProductsMenu(!showProductsMenu)}
              className="p-2 rounded-lg hover:bg-gray-100/50 transition-all duration-200 group"
              title={t('productsMenu.title')}
            >
              <svg className="w-4 h-4 text-gray-600 group-hover:text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>

            {/* Products Dropdown - Rendered via Portal */}
            {showProductsMenu && createPortal(
              <div
                ref={productsMenuRef}
                className="fixed w-64 bg-white/95 backdrop-blur-xl rounded-lg shadow-xl border border-gray-200/50 py-2"
                style={{
                  top: `${productsMenuPosition.top}px`,
                  left: `${productsMenuPosition.left}px`,
                  zIndex: 9999
                }}
              >
                <div className="px-4 py-3 border-b border-gray-100/50">
                  <h3 className="font-medium text-text-dark text-sm">{t('productsMenu.title')}</h3>
                  <p className="text-xs text-gray-500 mt-1">{t('productsMenu.subtitle')}</p>
                </div>

                <div className="py-1">
                  {visibleProducts.map((p) => {
                    const body = (
                      <>
                        <div className="w-8 h-8 rounded-md flex items-center justify-center mr-3">
                          <ProductIcon name={p.name} src={p.iconPath} />
                        </div>
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-gray-500">{describe(p, p.key ? t(`productsMenu.items.${p.key}`) : undefined)}</div>
                        </div>
                      </>
                    )
                    const cls = 'flex items-center px-4 py-2 text-sm text-text-dark hover:bg-gray-50/80 transition-all duration-150 group'
                    return p.external ? (
                      <a key={p.id} href={p.href(i18n.language)} target="_blank" rel="noopener noreferrer" onClick={() => setShowProductsMenu(false)} className={cls}>
                        {body}
                      </a>
                    ) : (
                      <Link key={p.id} to={p.href(i18n.language)} onClick={() => setShowProductsMenu(false)} className={cls}>
                        {body}
                      </Link>
                    )
                  })}
                </div>
              </div>,
              document.body
            )}
          </div>

          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center shadow-md border border-gray-200">
              <img
                src="/icon.png"
                alt={t('logo.alt')}
                className="w-4 h-4 rounded-sm"
              />
            </div>
            <div>
              <h1 className="text-base font-semibold text-text-dark">{t('logo.title')}</h1>
              <div className="w-full h-0.5 bg-linear-to-r from-primary to-secondary rounded-full opacity-40"></div>
            </div>
          </Link>
        </div>

        {/* Search Bar + Add Button */}
        <div className="flex-1 max-w-4xl ml-4 mr-4 flex items-center space-x-4">
          <div ref={searchRef} className="flex-1 max-w-2xl relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              placeholder={t('search.placeholder')}
              className="w-full pl-12 pr-20 py-2 bg-gray-100 border-2 border-gray-300 focus:border-primary/40 focus:bg-white rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none transition-all"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1 pointer-events-none">
              <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs text-gray-500">
                {navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}
              </kbd>
              <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs text-gray-500">
                K
              </kbd>
            </div>

            {/* Search Results Dropdown - Rendered via Portal */}
            {isSearchFocused && searchQuery && createPortal(
              <div
                ref={searchResultsRef}
                className="fixed bg-white rounded-lg shadow-xl border border-gray-200 max-h-[70vh] overflow-y-auto"
                style={{
                  top: `${searchResultsPosition.top}px`,
                  left: `${searchResultsPosition.left}px`,
                  width: `${searchResultsPosition.width}px`,
                  zIndex: 9999
                }}
              >
                {searchResults.length > 0 ? (
                  <div className="p-2">
                    {Object.entries(groupedResults).map(([category, items]) => (
                      <div key={category} className="mb-3 last:mb-0">
                        <div className="px-2.5 py-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                          <span className="text-sm">{getCategoryIcon(category as SearchCategory)}</span>
                          <span>{tSearch(`categories.${category}`, category)}</span>
                        </div>
                        <div className="space-y-0.5">
                          {items.map((item) => {
                            const globalIndex = searchResults.indexOf(item)
                            const isSelected = globalIndex === selectedIndex

                            return (
                              <button
                                key={item.id}
                                onClick={() => handleSelectResult(item)}
                                onMouseEnter={() => setSelectedIndex(globalIndex)}
                                className={`w-full flex items-center space-x-2.5 px-2.5 py-2 rounded-md text-left transition-colors ${
                                  isSelected
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <div className={`shrink-0 ${isSelected ? 'text-primary' : 'text-gray-400'}`}>
                                  {item.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">
                                    {getText(item.title, i18n.language, 'en') || item.title.en}
                                  </div>
                                  {item.description && (
                                    <div className="text-xs text-gray-500 truncate mt-0.5">
                                      {getText(item.description, i18n.language, 'en') || item.description.en}
                                    </div>
                                  )}
                                </div>
                                {item.external && (
                                  <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-400 text-sm">
                    {tSearch('noResults', 'No results found')}
                  </div>
                )}
              </div>,
              document.body
            )}
          </div>
        </div>

        {/* Right Actions */}
        <div className="flex items-center space-x-2">

          {/* User Menu */}
          <div className="relative" ref={userMenuContainerRef}>
            <button
              ref={userButtonRef}
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="p-1 rounded-lg hover:bg-gray-100/50 transition-all duration-200 group"
              title={t('user.menu.profile')}
            >
              <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-medium">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
            </button>

            {/* User Dropdown - Rendered via Portal */}
            {showUserMenu && createPortal(
              <div
                ref={userMenuRef}
                className="fixed w-56 bg-white/95 backdrop-blur-xl rounded-lg shadow-xl border border-gray-200/50 py-2"
                style={{
                  top: `${userMenuPosition.top}px`,
                  right: `${userMenuPosition.right}px`,
                  zIndex: 9999
                }}
              >
                <div className="px-4 py-3 border-b border-gray-100/50">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shadow-md">
                      <span className="text-white text-xs font-medium">
                        {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-text-dark text-sm">{user?.name || t('user.menu.profile')}</div>
                      <div className="text-xs text-gray-500">{user?.account || ''}</div>
                      <div className="text-xs text-blue-600 capitalize">
                        {user?.platform_role || ''} • {user?.type === 'local' ? t('user.menu.local') : user?.type === 'ldap' ? t('user.menu.ldap') : t('user.menu.oidc')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <Link
                    to="/settings/profile"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center px-4 py-2 text-sm text-text-dark hover:bg-gray-50/80 transition-all duration-150 group"
                  >
                    <div className="w-6 h-6 bg-blue-50 rounded-md flex items-center justify-center mr-3 group-hover:bg-blue-100 transition-colors">
                      <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    {t('user.menu.profile')}
                  </Link>
                  <a
                    href="https://docs.google.com/document/d/1GY3_gTNkjoPS3XBrCWl10c4uATxiOupCvAeMSVVsfJY/edit?tab=t.xin9hbg2jw6b"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 text-sm text-text-dark hover:bg-gray-50/80 transition-all duration-150 group"
                  >
                    <div className="w-6 h-6 bg-green-50 rounded-md flex items-center justify-center mr-3 group-hover:bg-green-100 transition-colors">
                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    {t('user.menu.help')}
                  </a>
                </div>

                <div className="border-t border-gray-100/50 pt-1">
                  <button
                    onClick={() => {
                      setShowUserMenu(false)
                      logout()
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50/80 transition-all duration-150 group"
                  >
                    <div className="w-6 h-6 bg-red-50 rounded-md flex items-center justify-center mr-3 group-hover:bg-red-100 transition-colors">
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    {t('user.menu.logout')}
                  </button>
                </div>
              </div>,
              document.body
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <CreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </header>
  )
}
